import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import EventIcon from '@mui/icons-material/Event'
import ConceptModelDiagram from 'features/taskManagement/ConceptModelDiagram'
import TaskAnalyticsPanel from 'features/taskManagement/components/TaskAnalyticsPanel'
import RemedialPerformancePanel from 'features/taskManagement/components/RemedialPerformancePanel'
import RemedialsReviewEntry from 'features/taskManagement/components/RemedialsReviewEntry'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { LIVE_STAGES, remedialsViewUrl } from 'features/actions/workspace/remedialsQuery'
import { hubCardCounts } from 'features/actions/workspace/remedialPerformance'
import { RemedialCountsExplainer } from 'features/actions/workspace/Explainers'
import { selectTaskStats, selectTaskStatsLoading } from 'features/taskManagement/ducks/selectors'
import { selectActionStats, selectStatsLoading as selectActionStatsLoading, selectActionsLoading } from 'features/actions/ducks/selectors'
import {
  selectIncidentStats,
  selectIncidentStatsLoading,
  selectIncidentsLoading,
  getIncidentStatsRoutine,
} from 'features/incidents/ducks'
import LinearProgress from '@mui/material/LinearProgress'
import { getTaskStatsRoutine } from 'features/taskManagement/ducks/actions'
import { getActionStatsRoutine, getActionsRoutine, selectActions } from 'features/actions/ducks'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { getIncidentsRoutine, selectIncidents } from 'features/incidents/ducks'
import IncidentsAPI from 'services/IncidentsAPI'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import CloseIncidentDialog from 'features/incidents/components/CloseIncidentDialog'
import { PRIVATE_PATHS } from 'utils/paths'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

/**
 * Overview tab for the Tasks & Actions hub.
 *
 * Three feature cards explain what each surface is and surface a few
 * headline numbers + a severity distribution bar. Below the cards a
 * combined "Needs attention" panel lifts the most urgent items across
 * Actions and Incidents so the user has a one-glance triage starting
 * point without having to flip between tabs.
 *
 * Stats are fetched at the hub level (TaskHub.js) — this component just
 * reads them. The "needs attention" lists are fetched here with tight
 * filters so they don't bloat the Redux store.
 */
const TaskHubOverview = ({ onNavigate }) => {
  const dispatch = useDispatch()
  const user = useSelector(getCurrentUser)
  const taskStats = useSelector(selectTaskStats)
  const actionStats = useSelector(selectActionStats)
  const incidentStats = useSelector(selectIncidentStats)
  const recentActions = useSelector(selectActions)
  const recentIncidents = useSelector(selectIncidents)
  const [perfScope, setPerfScope] = useState({ type: 'all', id: null, label: 'All Building Passports' })
  const [perfPeriod, setPerfPeriod] = useState({ preset: 'last_30_days' })
  const [showConcept, setShowConcept] = useState(() => {
    try { return window.localStorage.getItem('taskHubHideConcept') !== '1' } catch { return true }
  })
  const toggleConcept = () => setShowConcept(v => {
    const next = !v
    try { window.localStorage.setItem('taskHubHideConcept', next ? '0' : '1') } catch { /* ignore */ }
    return next
  })

  // Per-surface loading flags. We pair "loading" with "no cached data yet"
  // because a stats fetch already in flight from a previous visit might
  // already have a result we want to show — we only want skeletons on the
  // *first* paint, then live updates.
  const taskStatsLoading = useSelector(selectTaskStatsLoading)
  const actionStatsLoading = useSelector(selectActionStatsLoading)
  const incidentStatsLoading = useSelector(selectIncidentStatsLoading)
  const actionsListLoading = useSelector(selectActionsLoading)
  const incidentsListLoading = useSelector(selectIncidentsLoading)

  const tasksFirstPaint = !taskStats && taskStatsLoading
  const actionsFirstPaint = !actionStats && actionStatsLoading
  const incidentsFirstPaint = !incidentStats && incidentStatsLoading
  const attentionFirstPaint = (
    (!recentActions || recentActions.length === 0) && actionsListLoading
  ) && (
    (!recentIncidents || recentIncidents.length === 0) && incidentsListLoading
  )

  // Top-level progress: any loading anywhere shows the thin bar.
  const anyLoading = taskStatsLoading || actionStatsLoading || incidentStatsLoading
                  || actionsListLoading || incidentsListLoading

  // Stats for the three feature cards. Each child page (Tasks/Actions/
  // Incidents) re-fetches with its own filters when opened, so this just
  // populates the overview snapshot.
  useEffect(() => {
    dispatch(getTaskStatsRoutine())
    dispatch(getActionStatsRoutine({}))
    dispatch(getIncidentStatsRoutine({}))
  }, [dispatch])

  // Top-priority items only: critical/high actions and open incidents.
  // Per-page filters get re-applied when the user opens the tab itself.
  useEffect(() => {
    dispatch(getActionsRoutine({
      page: 1,
      per_page: 5,
      'filter[stage]': LIVE_STAGES.join(','),
      sort: 'severity_desc',
    }))
    dispatch(getIncidentsRoutine({
      page: 1,
      per_page: 5,
      'filter[status]': 'open',
    }))
  }, [dispatch])

  // Org-wide counts (across the user's accessible BPs) — matches the
  // org-wide pattern Actions/Incidents use, so card stats and the
  // progress bar are comparable. `myOpenTasks` is intentionally not used
  // here; this card shows the team workload, not just "yours".
  const tasksOpen     = taskStats?.openTasks ?? 0
  const tasksOverdue  = taskStats?.overdue ?? 0
  const tasksCritical = taskStats?.critical ?? 0
  const tasksDoneWk   = taskStats?.completedThisWeek ?? 0

  const remedialCounts = hubCardCounts(actionStats)
  const actionsOpen = remedialCounts.open
  const actionsDone = remedialCounts.resolved + remedialCounts.dismissed
  const goRemedials = (view, extra) => onNavigate(remedialsViewUrl({}, view, extra))

  const incidentsOpen      = incidentStats?.byStatus?.open ?? 0
  const incidentsCritical  = incidentStats?.bySeverity?.critical ?? 0
  const incidentsHigh      = incidentStats?.bySeverity?.high ?? 0
  const incidentsMedium    = incidentStats?.bySeverity?.medium ?? 0
  const incidentsLow       = incidentStats?.bySeverity?.low ?? 0
  const incidentsResolved  = incidentStats?.byStatus?.resolved ?? 0
  const incidentsClosed    = incidentStats?.byStatus?.closed ?? 0
  const incidentsDone      = incidentsResolved + incidentsClosed

  // Progress = fraction of items the user has moved out of "open" state.
  // Uses what the existing stats endpoints already return (no extra API
  // calls). Hidden gracefully when there's nothing to show a ratio for.
  const buildProgress = (open, done, doneLabel) => {
    const total = open + done
    if (total === 0) return null
    return {
      label: doneLabel,
      done,
      open,
      total,
      pct: Math.round((done / total) * 100),
    }
  }

  const greeting = useMemo(() => {
    const hour = new Date().getHours()
    if (hour < 5)  return 'Working late'
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }, [])

  // Card order mirrors the tab order: Incidents → Actions → Tasks. Reads
  // top-to-bottom as "what happened → what to decide → what to do".
  const cards = [
    {
      key: 'incidents',
      title: 'Incidents',
      tagline: 'Something happened. Capture it',
      description:
        'Log fires, leaks, faults, near-misses, complaints. Guru reads the narrative, suggests severity and category, and proposes remedials you can keep or drop before filing.',
      icon: ReportProblemOutlinedIcon,
      tint: '#9333ea',
      tintBg: '#f3e8ff',
      stats: [
        { label: 'Open',          value: incidentsOpen,     icon: HourglassEmptyIcon, tint: '#9333ea', onClick: () => onNavigate(`${PRIVATE_PATHS.incidents}?status=open`) },
        { label: 'Critical',      value: incidentsCritical, icon: LocalFireDepartmentIcon, tint: '#b00020', onClick: () => onNavigate(`${PRIVATE_PATHS.incidents}?severity=critical`) },
        { label: 'Resolved',      value: incidentsResolved, icon: CheckCircleOutlineIcon, tint: '#15803d', onClick: () => onNavigate(`${PRIVATE_PATHS.incidents}?status=resolved`) },
      ],
      breakdown: incidentsOpen > 0
        ? [
            { label: 'Critical', count: incidentsCritical, colour: '#b00020' },
            { label: 'High',     count: incidentsHigh,     colour: '#c2410c' },
            { label: 'Medium',   count: incidentsMedium,   colour: '#d97706' },
            { label: 'Low',      count: incidentsLow,      colour: '#15803d' },
          ]
        : null,
      progress: buildProgress(incidentsOpen, incidentsDone, 'Resolved'),
      cta: 'Open incidents',
      onClick: () => onNavigate('incidents'),
    },
    {
      key: 'actions',
      title: 'Remedials',
      tagline: 'Problems to put right',
      description:
        "Deficiencies and follow-ups Guru finds in your documents, audits and incidents. Each with a severity and a suggested fix. Review them, then mark each one as complete or turn it into a task.",
      icon: BoltOutlinedIcon,
      tint: '#D24724',
      tintBg: '#fff5f1',
      stats: [
        { label: 'Open', value: actionsOpen, icon: HourglassEmptyIcon, tint: '#D24724', onClick: () => goRemedials('all_open') },
        { label: 'Critical', value: remedialCounts.critical, icon: LocalFireDepartmentIcon, tint: RAG.critical.fg, onClick: () => goRemedials('all_open', { severity: 'critical' }) },
        { label: 'Overdue', value: remedialCounts.overdue, icon: EventBusyIcon, tint: RAG.critical.fg, onClick: () => goRemedials('overdue') },
        { label: 'Needs a decision', value: remedialCounts.needsDecision, icon: HelpOutlineIcon, tint: RAG.high.fg, onClick: () => goRemedials('needs_decision') },
        { label: 'Due this week', value: remedialCounts.dueThisWeek, icon: EventIcon, tint: RAG.medium.fg, onClick: () => goRemedials('due_this_week') },
        { label: 'Resolved', value: remedialCounts.resolved, icon: CheckCircleOutlineIcon, tint: RAG.low.fg, onClick: () => goRemedials('resolved') },
      ],
      breakdown: actionsOpen > 0
        ? [
            { label: 'Critical', count: remedialCounts.critical, colour: RAG.critical.fg },
            { label: 'High',     count: remedialCounts.high,     colour: RAG.high.fg },
            { label: 'Medium',   count: remedialCounts.medium,   colour: RAG.medium.fg },
            { label: 'Low',      count: remedialCounts.low,      colour: RAG.low.fg },
          ]
        : null,
      progress: buildProgress(actionsOpen, actionsDone, 'Resolved or dismissed'),
      explainer: <RemedialCountsExplainer />,
      cta: 'Open remedials',
      onClick: () => onNavigate('actions'),
    },
    {
      key: 'tasks',
      title: 'Tasks',
      tagline: 'Plan, schedule, and complete',
      description:
        'The day-to-day work board: owners, due dates, projects, approvals. This is where work actually gets done.',
      icon: AssignmentTurnedInOutlinedIcon,
      tint: '#33becc',
      tintBg: '#e6f7f9',
      stats: [
        { label: 'Open',         value: tasksOpen,     icon: HourglassEmptyIcon, tint: '#33becc', onClick: () => onNavigate(`${PRIVATE_PATHS.taskManagement}?tab=tasks&open=1`) },
        { label: 'Overdue',      value: tasksOverdue,  icon: EventBusyIcon,      tint: '#b00020', onClick: () => onNavigate(`${PRIVATE_PATHS.taskManagement}?tab=tasks&overdue=1`) },
        { label: 'Critical',     value: tasksCritical, icon: LocalFireDepartmentIcon, tint: PRIORITY_META.critical.fg, onClick: () => onNavigate(`${PRIVATE_PATHS.taskManagement}?tab=tasks&open=1&priority=critical`) },
        ...(tasksDoneWk !== null ? [{ label: 'Done this week', value: tasksDoneWk, icon: CheckCircleOutlineIcon, tint: '#15803d' }] : []),
      ],
      // Tasks don't carry severity; instead show a tiny ratio: critical vs rest.
      breakdown: tasksOpen > 0
        ? [
            { label: 'Critical', count: tasksCritical,                colour: PRIORITY_META.critical.fg },
            { label: 'Overdue',  count: tasksOverdue,                 colour: '#b00020' },
            { label: 'Other',    count: Math.max(0, tasksOpen - tasksCritical - tasksOverdue), colour: '#cbd5e1' },
          ]
        : null,
      progress: buildProgress(tasksOpen, tasksDoneWk, 'Done this week'),
      cta: 'Open tasks',
      onClick: () => onNavigate('tasks'),
    },
  ]

  // Tracks rows the user dismissed/closed inline so the panel updates
  // without round-tripping the list endpoint. The full Actions/Incidents
  // tabs re-fetch on entry, so the global state stays consistent.
  const [removedIds, setRemovedIds] = useState(new Set())
  const [busyIds, setBusyIds] = useState(new Set())

  const setBusy = (key, on) => {
    setBusyIds((prev) => {
      const next = new Set(prev)
      if (on) next.add(key); else next.delete(key)
      return next
    })
  }

  // Combined "needs attention" feed — top critical/high actions and open
  // incidents, capped to a handful so the panel stays scannable.
  const attentionItems = useMemo(() => {
    const items = []
    ;(recentActions || []).slice(0, 4).forEach((a) => items.push({
      kind: 'action',
      id: a.id,
      title: a.title,
      severity: a.severity,
      meta: a.buildingPassport?.name,
      onClick: () => onNavigate(`/actions/${a.id}`),
    }))
    ;(recentIncidents || []).slice(0, 4).forEach((i) => {
      // Incidents don't carry a separate title — synthesise a readable
      // summary from the narrative (first sentence-ish) so the row is
      // identifiable, and fall back to the category if narrative is empty.
      const narrative = (i.narrative || '').trim()
      const summary = narrative
        ? (narrative.slice(0, 80).trim() + (narrative.length > 80 ? '…' : ''))
        : (i.category ? `${i.category.replace(/_/g, ' ')} incident` : 'Incident')
      items.push({
        kind: 'incident',
        id: i.id,
        title: summary,
        severity: i.severity,
        meta: i.buildingPassport?.name,
        // Already-closed incidents shouldn't reach the open list, but be
        // defensive in case the cached payload is stale.
        canClose: i.permissions?.canClose !== false && i.status !== 'closed',
        onClick: () => onNavigate(`/incidents/${i.id}`),
      })
    })
    return items
      .filter((it) => !removedIds.has(`${it.kind}:${it.id}`))
      .slice(0, 6)
  }, [recentActions, recentIncidents, onNavigate, removedIds])

  const [closingIncident, setClosingIncident] = useState(null)

  const requestCloseIncident = (item) => {
    const incidentRow = (recentIncidents || []).find((i) => i.id === item.id) || {}
    const openActionsCount =
      incidentRow.openActionsCount
      ?? Math.max(
        0,
        (incidentRow.actionsCount ?? 0)
        - (incidentRow.resolvedActionsCount ?? 0)
        - (incidentRow.dismissedActionsCount ?? 0)
      )
    setClosingIncident({ ...item, openActionsCount, title: incidentRow.narrative ? `${incidentRow.narrative.slice(0, 60)}${incidentRow.narrative.length > 60 ? '…' : ''}` : item.title })
  }

  const performCloseIncident = async () => {
    if (!closingIncident) return
    const item = closingIncident
    const key = `${item.kind}:${item.id}`
    setBusy(key, true)
    try {
      await IncidentsAPI.close(item.id)
      setRemovedIds((prev) => new Set(prev).add(key))
      dispatch(getIncidentStatsRoutine({}))
      toast.info('Incident closed.')
      setClosingIncident(null)
    } catch (e) {
      toast.error('Could not close this incident. Please try again.')
    } finally {
      setBusy(key, false)
    }
  }

  const firstName = (user?.firstName || user?.first_name || '').trim()

  return (
    <Wrapper>
      {/* Thin top-of-page progress bar so the user can see the page is
          working even when stats / lists are still in flight. Reserves
          its height to avoid jumping the layout when it disappears. */}
      <ProgressLane aria-hidden={!anyLoading}>
        {anyLoading && <LinearProgress sx={{ height: 3, borderRadius: 999, '& .MuiLinearProgress-bar': { background: '#D24724' }, background: '#fff1ec' }} />}
      </ProgressLane>

      <Hero>
        <Greeting>
          {greeting}{firstName ? `, ${firstName}` : ''}.
        </Greeting>
        <HeroSub>
          How it fits together: <strong>obligations</strong> are what each building must do,{' '}
          <strong>tasks</strong> are the work that meets them, and <strong>remedials</strong> are
          problems to put right. Raised by documents, incidents, accidents or a completed task. And
          both obligations and remedials are closed by doing a task.
        </HeroSub>

        {showConcept ? (
          <ConceptBlock>
            <ConceptToggle type='button' onClick={toggleConcept}>Hide diagram</ConceptToggle>
            <ConceptModelDiagram />
          </ConceptBlock>
        ) : (
          <ShowConceptBtn type='button' onClick={toggleConcept}>
            Show “how it fits together” diagram
          </ShowConceptBtn>
        )}
      </Hero>

      <TaskAnalyticsPanel onNavigate={onNavigate} scope={perfScope} onScopeChange={setPerfScope} period={perfPeriod} onPeriodChange={setPerfPeriod} />

      <RemedialPerformancePanel onNavigate={onNavigate} scope={perfScope} onScopeChange={setPerfScope} period={perfPeriod} onPeriodChange={setPerfPeriod} />

      <RemedialsReviewEntry />

      <Cards>
        {cards.map((c) => {
          const Icon = c.icon
          const total = c.breakdown?.reduce((sum, b) => sum + (b.count || 0), 0) || 0
          const isFirstPaint =
            (c.key === 'tasks'     && tasksFirstPaint)     ||
            (c.key === 'actions'   && actionsFirstPaint)   ||
            (c.key === 'incidents' && incidentsFirstPaint)
          return (
            <Card key={c.key} onClick={c.onClick}>
              <CardHeader>
                <CardIcon $bg={c.tintBg} $tint={c.tint}>
                  <Icon style={{ fontSize: 26 }} />
                </CardIcon>
                <CardHeaderText>
                  <CardTitle>{c.title}</CardTitle>
                  <CardTagline>{c.tagline}</CardTagline>
                </CardHeaderText>
                {c.explainer && (
                  <ExplainerSlot onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    {c.explainer}
                  </ExplainerSlot>
                )}
              </CardHeader>

              <CardDesc>{c.description}</CardDesc>

              <CardStats>
                {c.stats.map((s) => {
                  const StatIcon = s.icon
                  return (
                    <Stat
                      key={s.label}
                      role='button'
                      tabIndex={0}
                      title={`View ${s.label.toLowerCase()}`}
                      onClick={(e) => { e.stopPropagation(); (s.onClick || c.onClick)() }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); (s.onClick || c.onClick)() } }}
                    >
                      <StatIcon style={{ fontSize: 14, color: s.tint, flexShrink: 0 }} />
                      {isFirstPaint
                        ? <SkeletonValue />
                        : <StatValue>{s.value}</StatValue>}
                      <StatLabel>{s.label}</StatLabel>
                    </Stat>
                  )
                })}
              </CardStats>

              {isFirstPaint ? (
                <>
                  <Progress>
                    <ProgressHead>
                      <SkeletonLine $w="40%" />
                      <SkeletonLine $w="30%" />
                    </ProgressHead>
                    <ProgressTrack>
                      <SkeletonShimmer />
                    </ProgressTrack>
                  </Progress>
                  <Distribution>
                    <DistributionBar>
                      <SkeletonShimmer />
                    </DistributionBar>
                  </Distribution>
                </>
              ) : (
                <>
                  {c.progress && (
                    <Progress>
                      <ProgressHead>
                        <ProgressLabel>{c.progress.label}</ProgressLabel>
                        <ProgressNumbers>
                          <strong>{c.progress.done}</strong> of {c.progress.total} ({c.progress.pct}%)
                        </ProgressNumbers>
                      </ProgressHead>
                      <ProgressTrack>
                        <ProgressFill style={{ width: `${c.progress.pct}%`, background: c.tint }} />
                      </ProgressTrack>
                    </Progress>
                  )}

                  {c.breakdown && total > 0 && (
                    <Distribution>
                      <DistributionBar>
                        {c.breakdown.map((b) =>
                          b.count > 0 ? (
                            <DistributionSegment
                              key={b.label}
                              style={{ width: `${(b.count / total) * 100}%`, background: b.colour }}
                              title={`${b.label}: ${b.count}`}
                            />
                          ) : null
                        )}
                      </DistributionBar>
                      <DistributionLegend>
                        {c.breakdown.map((b) => (
                          <LegendChip key={b.label}>
                            <LegendSwatch style={{ background: b.colour }} />
                            {b.label} <strong>{b.count}</strong>
                          </LegendChip>
                        ))}
                      </DistributionLegend>
                    </Distribution>
                  )}
                </>
              )}

              <CardFooter>
                <CardCta type='button' $tint={c.tint} onClick={(e) => { e.stopPropagation(); c.onClick() }}>
                  {c.cta} <ArrowForwardIcon style={{ fontSize: 14 }} />
                </CardCta>
              </CardFooter>
            </Card>
          )
        })}
      </Cards>

      {(attentionItems.length > 0 || attentionFirstPaint) && (
        <Attention>
          <AttentionHead>
            <AttentionTitle>
              <LocalFireDepartmentIcon style={{ fontSize: 18, color: '#c2410c' }} />
              Needs attention now
            </AttentionTitle>
            <AttentionHint>
              {attentionFirstPaint
                ? 'Loading the most urgent items across the Building Passports you can see…'
                : 'Top open remedials and incidents across the Building Passports you can see.'}
            </AttentionHint>
          </AttentionHead>
          <AttentionList>
            {attentionFirstPaint && [0, 1, 2, 3].map((i) => (
              <AttentionItem key={`skeleton-${i}`} as="div" style={{ cursor: 'default' }}>
                <SkeletonLine $w="60px" />
                <SkeletonLine $w="70%" />
                <SkeletonLine $w="40%" />
                <SkeletonLine $w="50px" />
              </AttentionItem>
            ))}
            {!attentionFirstPaint && attentionItems.map((it) => {
              const key = `${it.kind}:${it.id}`
              const isBusy = busyIds.has(key)
              const canClear = it.kind === 'incident' && it.canClose
              const clearLabel = 'Close'
              const onClear = (e) => {
                e.stopPropagation()
                requestCloseIncident(it)
              }
              return (
                <AttentionItem
                  key={`${it.kind}-${it.id}`}
                  onClick={it.onClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') it.onClick() }}
                >
                  <ItemKind $kind={it.kind}>
                    {it.kind === 'action' ? <BoltOutlinedIcon style={{ fontSize: 14 }} /> : <ReportProblemOutlinedIcon style={{ fontSize: 14 }} />}
                    {it.kind === 'action' ? 'Remedial' : 'Incident'}
                  </ItemKind>
                  <ItemTitle>{it.title}</ItemTitle>
                  <ItemMeta>{it.meta}</ItemMeta>
                  {it.severity && <SeverityChip $sev={it.severity}>{it.severity}</SeverityChip>}
                  {canClear && (
                    <ItemClearBtn
                      type="button"
                      onClick={onClear}
                      disabled={isBusy}
                      title={`${clearLabel}. Clears it from this list and the open queue`}
                      aria-label={`${clearLabel} ${it.kind}`}
                    >
                      <CloseIcon style={{ fontSize: 14 }} />
                    </ItemClearBtn>
                  )}
                </AttentionItem>
              )
            })}
          </AttentionList>
        </Attention>
      )}

      <CloseIncidentDialog
        open={!!closingIncident}
        incidentTitle={closingIncident?.title}
        openActionsCount={closingIncident?.openActionsCount || 0}
        onCancel={() => setClosingIncident(null)}
        onConfirm={performCloseIncident}
      />
    </Wrapper>
  )
}

export default TaskHubOverview

const Wrapper = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

/* Reserves a 3px lane for the top progress bar so layout doesn't jump
   when loading toggles. */
const ProgressLane = styled.div`
  height: 3px;
`

/* Skeleton primitives. The shimmer keyframe is shared so all skeletons
   pulse in sync. */
const shimmer = keyframes`
  0%   { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`

const SkeletonValue = styled.span`
  display: inline-block;
  width: 24px;
  height: 14px;
  border-radius: 3px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`

const SkeletonLine = styled.span`
  display: inline-block;
  width: ${({ $w }) => $w || '60%'};
  height: 10px;
  border-radius: 3px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`

const SkeletonShimmer = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Greeting = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.01em;
  @media (max-width: 768px) { font-size: 20px; }
`

const HeroSub = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
  max-width: 820px;
  line-height: 1.55;
  strong { color: #0f172a; font-weight: 600; }
  @media (max-width: 768px) { font-size: 13px; }
`

const ConceptBlock = styled.div`
  display: flex;
  flex-direction: column;
`

const ConceptToggle = styled.button`
  align-self: flex-end;
  font: inherit;
  cursor: pointer;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  padding: 2px 4px;
  margin-bottom: 2px;
  &:hover { color: #0f172a; text-decoration: underline; }
`

const ShowConceptBtn = styled.button`
  align-self: flex-start;
  font: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f4f5f9;
  border: 1px solid #e6e8f0;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  color: #344054;
  padding: 6px 14px;
  &:hover { border-color: #cfd3e0; color: #0f172a; }
`


const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }
`

const Card = styled.div`
  text-align: left;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
    border-color: #d1d5db;
  }

  @media (max-width: 768px) {
    padding: 14px;
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const CardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ $bg }) => $bg};
  color: ${({ $tint }) => $tint};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const CardHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const CardTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #0f172a;
`

const CardTagline = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`

const CardDesc = styled.p`
  font-size: 13px;
  line-height: 1.55;
  color: #475569;
  margin: 0;
  min-height: calc(13px * 1.55 * 3);
`

const CardStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 12px;
  padding: 10px 0 4px 0;
  border-top: 1px solid #f1f5f9;
`

const Stat = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  min-width: 0;
  cursor: pointer;
  border-radius: 6px;
  padding: 3px 5px;
  margin: -3px -5px;
  transition: background 0.12s;

  &:hover { background: #f1f5f9; }
  &:focus-visible { outline: 2px solid #94a3b8; outline-offset: 1px; }
`

const StatValue = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
`

const StatLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ProgressHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: #64748b;
`

const ProgressLabel = styled.span`
  font-weight: 600;
  color: #475569;
`

const ProgressNumbers = styled.span`
  strong { color: #0f172a; font-weight: 700; }
`

const ProgressTrack = styled.div`
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
`

const Distribution = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const DistributionBar = styled.div`
  display: flex;
  height: 8px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
`

const DistributionSegment = styled.div`
  height: 100%;
  transition: width 0.3s ease;
`

const DistributionLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-size: 11px;
  color: #64748b;
`

const LegendChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  strong { color: #0f172a; font-weight: 600; }
`

const LegendSwatch = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 3px;
  flex-shrink: 0;
`

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const CardCta = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $tint }) => $tint};
  &:hover { text-decoration: underline; }
`

const ExplainerSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`

const Attention = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  @media (max-width: 768px) { padding: 14px; }
`

const AttentionHead = styled.div`
  margin-bottom: 12px;
`

const AttentionTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
`

const AttentionHint = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`

const AttentionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const AttentionItem = styled.div`
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr) auto auto auto;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: #f8fafc; }

  @media (max-width: 768px) {
    grid-template-columns: 76px minmax(0, 1fr) auto auto;
    gap: 8px;
    padding: 10px 6px;
    /* Drop the BP meta column on narrow screens — title stays readable. */
    > *:nth-child(3) { display: none; }
  }
`

const ItemClearBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: transparent;
  border: 1px solid transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  flex-shrink: 0;
  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #fecaca;
    color: #b91c1c;
  }
  &:disabled { opacity: 0.4; cursor: progress; }
`

const ItemKind = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ $kind }) => ($kind === 'action' ? '#D24724' : '#9333ea')};
`

const ItemTitle = styled.div`
  font-size: 13px;
  color: #0f172a;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ItemMeta = styled.div`
  min-width: 0;
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SEVERITY_TINT = {
  critical: { bg: '#fdecea', fg: '#b00020' },
  high:     { bg: '#ffedd5', fg: '#c2410c' },
  medium:   { bg: '#fef3c7', fg: '#d97706' },
  low:      { bg: '#dcfce7', fg: '#15803d' },
}

const SeverityChip = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $sev }) => SEVERITY_TINT[$sev]?.bg || '#f1f5f9'};
  color: ${({ $sev }) => SEVERITY_TINT[$sev]?.fg || '#475569'};
`
